import { AnyAction } from "redux";
import { addDays, format } from "date-fns";

import {
  B2B_LOCATIONS_ENDPOINT,
  B2B_LOCATION_ENDPOINT,
  B2B_CONTACT_FORM_ENDPOINT,
  BOOKING_AVAILABILITY_ENDPOINT,
  B2B_RESERVATION_ENDPOINT,
  B2B_PAYMENT_METHODS_ENDPOINT,
  B2B_PRICING_ENDPOINT,
  B2B_REGISTER_CLIENT_ENDPOINT,
  B2B_REGISTER_COMPANY_ENDPOINT,
  B2B_CHECKOUT_ENDPOINT,
  B2B_PREMISES_SERVICES_ENDPOINT,
  B2B_CANCEL_RESERVATION_ENDPOINT,
  B2B_VOUCHER_ENDPOINT,
  B2B_AGENTS_ENDPOINT,
  GET_A_QUOTE_ENDPOINT,
  B2B_FREQUENCIES_ENDPOINT,
  B2B_PLANS_ENDPOINT,
} from "@config/endpoints";
import { FeatureTogglesType } from "@config/featureToggles";

import {
  PREMISES,
  FREQUENCY,
  Task,
  NonStandardFrequencyShifts,
  PremisesEquipments,
  KEY_PICK_B2B,
} from "@typings/b2b";
import {
  AGENTS_FILTER,
  AGENT_PREFERENCE,
  CALENDAR_WEEK_SHIFT,
} from "@typings/booking";
import { SimpleObject, PAYMENT_METHODS } from "@typings/globals";
import {
  ThunkActionDispatch,
  ThunkActionOptions,
} from "@typings/reduxThunkActions";
import { VoucherInfo } from "@typings/bookingsToValidate";

import { ReduxActionNameCreator } from "@services/ReduxActionNameCreator";
import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";
import { ASYNC_ACTION_TYPES } from "@services/FetchFacade";
import { buildUrl } from "@services/BuildUrl";
import { DATE_FORMAT } from "@services/DateFormatter";

import { asyncActionCreator } from "@app/services/AsyncActionCreator";
import { genericPendingAction } from "@app/services/GenericPendingAction";
import {
  abortControllerHelper,
  setLocalStorageHelper,
  setFromLocalStorageHelper,
  deleteLocalStorageHelper,
} from "@app/redux/helpers";

import { IStore } from "../reducers";

import {
  getCalendarWeek,
  getExecutionDateTime,
  getServiceLocationId,
} from "./b2b.selectors";
import {
  getAddLocationEndpointRequest,
  getAvailabilityEndpointRequest,
  getReservationEndpointRequest,
  getPricingEndpointRequest,
  getRegisterClientEndpointRequest,
  getRegisterCompanyEndpointRequest,
  getCheckoutEndpointRequest,
  getGetAQuoteEndpointRequest,
} from "./b2b.repository";
import { RegistrationForm, LOCAL_STORAGE_KEY, B2BReducer } from "./b2b.typings";

const action = new ReduxActionNameCreator("b2b");

const abortControllers = {
  fetchPreviousAgents: abortControllerHelper(),
  fetchLocations: abortControllerHelper(),
  fetchBookingAvailability: abortControllerHelper(),
  fetchBookingAvailabilityAdmin: abortControllerHelper(),
  fetchPaymentMethods: abortControllerHelper(),
  fetchPricings: abortControllerHelper(),
  fetchPremisesServices: abortControllerHelper(),
  fetchVoucher: abortControllerHelper(),
  fetchPlans: abortControllerHelper(),
};

export const B2B_ACTIONS = {
  SET_PREMISES: action.make("set premises"),
  SET_SURFACE_AREA: action.make("set surface area"),
  SET_PREMISES_EQUIPMENTS: action.make("set premises equipments"),
  SET_RECOMMENDED_DURATIONS_APPLIED: action.make(
    "set recommended durations applied",
  ),
  SET_RECOMMENDED_DURATION: action.make("set recommended duration"),
  SET_TASK: action.make("set task"),
  SET_PREMISES_SERVICE: action.make("set premises service"),
  SET_FREQUENCY: action.make("set frequency"),
  SET_I_DONT_KNOW_FREQUENCY_NOTIFICATION: action.make(
    "set i dont know frequency notification",
  ),
  SET_I_DONT_KNOW_DURATION_NOTIFICATION: action.make(
    "set i dont know duration notification",
  ),
  SET_HAS_CLEANING_MATERIALS: action.make("set has cleaning materials"),
  SET_CONTACT_AFTER_BOOKING: action.make("set contact after booking"),
  SET_EXECUTION: action.make("set execution"),
  SET_NON_STANDARD_FREQUENCY_CLEANINGS: action.make(
    "set non standard frequency cleaning",
  ),
  SET_PRESELECTED_AGENT_UUID: action.make("set preselected agent uuid"),
  FETCH_PREVIOUS_AGENTS: action.make("fetch previous agents"),
  SET_AGENT_PREFERENCE: action.make("set agent preference"),
  SET_IS_CHOOSING_ANY_BATMAID: action.make("set is choosing any batmaid"),
  SET_LOCATION_UUID: action.make("set location uuid"),
  ADD_LOCATION: action.make("add location"),
  FETCH_LOCATIONS: action.make("fetch locations"),
  SEND_B2B_CONTACT_FORM: action.make("send contact form"),
  FETCH_AVAILABILITY: action.make("fetch availability"),
  FETCH_AVAILABILITY_ADMIN: action.make("fetch availability admin"),
  FETCH_CUSTOM_AVAILABILITY: action.make("fetch custom availability"),
  RESET_AVAILABILITY: action.make("reset availability"),
  SET_WEEK_START_DATE: action.make("set week start date"),
  SET_REGISTRATION_FORM: action.make("set registration form"),
  MAKE_RESERVATION: action.make("make reservation"),
  SET_SERVICE_LOCATION_ID: action.make("set service location id"),
  SET_SPECIAL_INSTRUCTIONS: action.make("set special instructions"),
  SET_KEY_PICK: action.make("set key pick"),
  SET_LOCATION_COMMENT: action.make("set location comment"),
  FETCH_PAYMENT_METHODS: action.make("fetch payment methods"),
  SET_PAYMENT_METHOD: action.make("set payment method"),
  FETCH_PRICINGS: action.make("fetch pricings"),
  REGISTER_CLIENT: action.make("register client"),
  REGISTER_COMPANY: action.make("register company"),
  CHECKOUT: action.make("checkout"),
  FETCH_PREMISES_SERVICES: action.make("fetch premises services"),
  CANCEL_RESERVATION: action.make("cancel reservation"),
  SET_IN_LOCAL_STORAGE: action.make("set in local storage"),
  SET_FROM_LOCAL_STORAGE: action.make("set from local storage"),
  DELETE_LOCAL_STORAGE: action.make("delete local storage"),
  FETCH_VOUCHER: action.make("fetch voucher"),
  RESET_VOUCHER: action.make("reset voucher"),
  SET_VOUCHER: action.make("set voucher"),
  SET_QUERY_PARAMS: action.make("set query params"),
  LEAVE_PROCESS: action.make("leave process"),
  GET_A_QUOTE: action.make("get a quote"),
  SET_ADDITIONAL_INFO: action.make("set additional info"),
  FETCH_FREQUENCIES: action.make("fetch frequencies"),
  FETCH_PLANS: action.make("fetch plans"),
};

export const setPremises = (payload: PREMISES): AnyAction => ({
  type: B2B_ACTIONS.SET_PREMISES,
  payload,
});

export const setPremisesService =
  (payload: Task) =>
  (dispatch: ThunkActionDispatch, store: () => IStore): AnyAction =>
    dispatch({
      type: B2B_ACTIONS.SET_PREMISES_SERVICE,
      payload: {
        [store().b2b.get("premises")]: {
          [payload.service]: {
            ...payload,
          },
        },
      },
    });

export const setTask = (payload: Task): AnyAction => ({
  type: B2B_ACTIONS.SET_TASK,
  payload,
});

export const setIDontKnowFrequencyNotification = (
  payload: boolean,
): AnyAction => ({
  type: B2B_ACTIONS.SET_I_DONT_KNOW_FREQUENCY_NOTIFICATION,
  payload,
});

export const setIDontKnowDurationNotification = (
  payload: boolean,
): AnyAction => ({
  type: B2B_ACTIONS.SET_I_DONT_KNOW_DURATION_NOTIFICATION,
  payload,
});

export const setHasCleaningMaterials = (payload: boolean): AnyAction => ({
  type: B2B_ACTIONS.SET_HAS_CLEANING_MATERIALS,
  payload,
});

export const setContactAfterBooking = (payload: boolean): AnyAction => ({
  type: B2B_ACTIONS.SET_CONTACT_AFTER_BOOKING,
  payload,
});

export const setExecution = (
  payload: Partial<B2BReducer["execution"]>,
): AnyAction => ({
  type: B2B_ACTIONS.SET_EXECUTION,
  payload,
});

export const setPremisesEquipments = (
  payload: PremisesEquipments,
): AnyAction => ({
  type: B2B_ACTIONS.SET_PREMISES_EQUIPMENTS,
  payload,
});

export const setSurfaceArea = (payload: number): AnyAction => ({
  type: B2B_ACTIONS.SET_SURFACE_AREA,
  payload,
});

export const setRecommendedDuration = (payload: number): AnyAction => ({
  type: B2B_ACTIONS.SET_RECOMMENDED_DURATION,
  payload,
});

export const setRecommendedDurationsApplied = (
  payload: boolean,
): AnyAction => ({
  type: B2B_ACTIONS.SET_RECOMMENDED_DURATIONS_APPLIED,
  payload,
});

export const setNonStandardFrequencyShifts = (
  payload: NonStandardFrequencyShifts[],
): AnyAction => ({
  type: B2B_ACTIONS.SET_NON_STANDARD_FREQUENCY_CLEANINGS,
  payload,
});

export const fetchPreviousAgents =
  (filter: AGENTS_FILTER, opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator(
      {
        url: B2B_AGENTS_ENDPOINT.replace("{filterValue}", filter).replace(
          "{serviceLocationIdValue}",
          String(getServiceLocationId(store())),
        ),
        action: B2B_ACTIONS.FETCH_PREVIOUS_AGENTS,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchPreviousAgents(),
        }),
      },
    );

    return dispatch(action);
  };

export const setAgentPreference = (
  agentPreference: AGENT_PREFERENCE,
): AnyAction => ({
  type: B2B_ACTIONS.SET_AGENT_PREFERENCE,
  payload: {
    agentPreference,
  },
});

export const setPreselectedAgentUuid = (
  preselectedAgentUuid: string,
): AnyAction => ({
  type: B2B_ACTIONS.SET_PRESELECTED_AGENT_UUID,
  payload: {
    preselectedAgentUuid,
  },
});

export const setIsChoosingAnyBatmaid = (payload: boolean): AnyAction => ({
  type: B2B_ACTIONS.SET_IS_CHOOSING_ANY_BATMAID,
  payload,
});

export const sendB2BContactForm =
  (body: SimpleObject) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      action: B2B_ACTIONS.SEND_B2B_CONTACT_FORM,
      url: B2B_CONTACT_FORM_ENDPOINT,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const addLocation =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getAddLocationEndpointRequest(store());

    const action = await asyncActionCreator({
      url: B2B_LOCATION_ENDPOINT,
      action: B2B_ACTIONS.ADD_LOCATION,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const fetchLocations =
  (opts?: ThunkActionOptions) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator(
      {
        url: B2B_LOCATIONS_ENDPOINT,
        action: B2B_ACTIONS.FETCH_LOCATIONS,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchLocations(),
        }),
      },
    );

    return dispatch(action);
  };

export const setLocationUuid = (payload: string): AnyAction => ({
  type: B2B_ACTIONS.SET_LOCATION_UUID,
  payload,
});

export const fetchBookingAvailability =
  (serviceLocationId: number, opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(B2B_ACTIONS.FETCH_AVAILABILITY));

    const body = getAvailabilityEndpointRequest(store(), serviceLocationId);

    const action = await asyncActionCreator(
      {
        url: BOOKING_AVAILABILITY_ENDPOINT,
        action: B2B_ACTIONS.FETCH_AVAILABILITY,
        method: ASYNC_ACTION_TYPES.POST,
        body,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchBookingAvailability(),
        }),
      },
    );

    return dispatch(action);
  };

export const fetchCustomBookingAvailability =
  (
    serviceLocationId: number,
    chunkSize = 3,
    customWeekStartDate?: string,
    opts?: ThunkActionOptions,
  ) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(B2B_ACTIONS.FETCH_CUSTOM_AVAILABILITY));

    const body = getAvailabilityEndpointRequest(
      store(),
      serviceLocationId,
      customWeekStartDate,
    );

    const action = await asyncActionCreator(
      {
        url: BOOKING_AVAILABILITY_ENDPOINT,
        action: B2B_ACTIONS.FETCH_CUSTOM_AVAILABILITY,
        method: ASYNC_ACTION_TYPES.POST,
        body,
        params: {
          chunkSize,
          isExtraRequest: !!customWeekStartDate,
        },
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchBookingAvailability(),
        }),
      },
    );

    return dispatch(action);
  };

export const fetchBookingAvailabilityAdmin =
  (serviceLocationId: number, opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(B2B_ACTIONS.FETCH_AVAILABILITY));

    const body = getAvailabilityEndpointRequest(store(), serviceLocationId);

    const action = await asyncActionCreator(
      {
        url: BOOKING_AVAILABILITY_ENDPOINT,
        action: B2B_ACTIONS.FETCH_AVAILABILITY_ADMIN,
        method: ASYNC_ACTION_TYPES.POST,
        body,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchBookingAvailabilityAdmin(),
        }),
      },
    );

    return dispatch(action);
  };

export const resetAvailability = (): AnyAction => ({
  type: B2B_ACTIONS.RESET_AVAILABILITY,
});

export const setWeekStartDate = (payload: string): AnyAction => ({
  type: B2B_ACTIONS.SET_WEEK_START_DATE,
  payload,
});

export const setNextWeekStartDate =
  (payload: CALENDAR_WEEK_SHIFT) =>
  (dispatch: ThunkActionDispatch, store: () => IStore): AnyAction =>
    dispatch({
      type: B2B_ACTIONS.SET_WEEK_START_DATE,
      payload: getCalendarWeek(store(), payload),
    });

export const setRegistrationForm = (
  payload: Partial<RegistrationForm>,
): AnyAction => ({
  type: B2B_ACTIONS.SET_REGISTRATION_FORM,
  payload,
});

export const makeReservation =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getReservationEndpointRequest(store());

    const action = await asyncActionCreator({
      url: B2B_RESERVATION_ENDPOINT,
      action: B2B_ACTIONS.MAKE_RESERVATION,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const setServiceLocationId = (payload: number): AnyAction => ({
  type: B2B_ACTIONS.SET_SERVICE_LOCATION_ID,
  payload,
});

export const setSpecialInstructions = (payload: string): AnyAction => ({
  type: B2B_ACTIONS.SET_SPECIAL_INSTRUCTIONS,
  payload,
});

export const setKeyPick = (payload: KEY_PICK_B2B): AnyAction => ({
  type: B2B_ACTIONS.SET_KEY_PICK,
  payload,
});

export const setLocationComment = (payload: string): AnyAction => ({
  type: B2B_ACTIONS.SET_LOCATION_COMMENT,
  payload,
});
export const fetchPaymentMethods =
  (opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator(
      {
        url: B2B_PAYMENT_METHODS_ENDPOINT.replace(
          "{reservationUuid}",
          store().b2b.get("reservationUuid"),
        ).replace(
          "{displayedPriceValue}",
          store().b2b.getIn(["pricing", "displayedPriceForFirstBooking"]),
        ),
        action: B2B_ACTIONS.FETCH_PAYMENT_METHODS,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchPaymentMethods(),
        }),
      },
    );

    return dispatch(action);
  };

export const fetchPricings =
  (opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(B2B_ACTIONS.FETCH_PRICINGS));

    const body = getPricingEndpointRequest(store());

    const action = await asyncActionCreator(
      {
        url: B2B_PRICING_ENDPOINT,
        action: B2B_ACTIONS.FETCH_PRICINGS,
        method: ASYNC_ACTION_TYPES.POST,
        body,
      },
      {
        ...(opts?.abortEnabled && { signal: abortControllers.fetchPricings() }),
      },
    );

    return dispatch(action);
  };

export const fetchPremisesServices =
  (opts?: ThunkActionOptions) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator(
      {
        url: B2B_PREMISES_SERVICES_ENDPOINT,
        action: B2B_ACTIONS.FETCH_PREMISES_SERVICES,
        method: ASYNC_ACTION_TYPES.GET,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchPremisesServices(),
        }),
      },
    );

    return dispatch(action);
  };

export const registerClient =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getRegisterClientEndpointRequest(store());

    const action = await asyncActionCreator({
      url: B2B_REGISTER_CLIENT_ENDPOINT,
      action: B2B_ACTIONS.REGISTER_CLIENT,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const setPaymentMethod = (
  payload: PAYMENT_METHODS | null,
): AnyAction => ({
  type: B2B_ACTIONS.SET_PAYMENT_METHOD,
  payload,
});

export const registerCompany =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getRegisterCompanyEndpointRequest(store());

    const action = await asyncActionCreator({
      url: B2B_REGISTER_COMPANY_ENDPOINT,
      action: B2B_ACTIONS.REGISTER_COMPANY,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const checkout =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getCheckoutEndpointRequest(store());

    const action = await asyncActionCreator({
      url: B2B_CHECKOUT_ENDPOINT.replace(
        "{b2bReservationUuid}",
        store().b2b.get("reservationUuid"),
      ),
      action: B2B_ACTIONS.CHECKOUT,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const cancelReservation =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: B2B_CANCEL_RESERVATION_ENDPOINT.replace(
        "{b2bReservationUuid}",
        store().b2b.get("reservationUuid"),
      ),
      action: B2B_ACTIONS.CANCEL_RESERVATION,
      method: ASYNC_ACTION_TYPES.DELETE,
    });

    return dispatch(action);
  };

export const setQueryParams = (payload: string): AnyAction => ({
  type: B2B_ACTIONS.SET_QUERY_PARAMS,
  payload,
});

export const setLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch, store: () => IStore): void => {
    setLocalStorageHelper(
      dispatch,
      B2B_ACTIONS.SET_IN_LOCAL_STORAGE,
      store().b2b,
      LOCAL_STORAGE_KEY,
    );
  };

export const setFromLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch): void => {
    setFromLocalStorageHelper(
      dispatch,
      B2B_ACTIONS.SET_FROM_LOCAL_STORAGE,
      LOCAL_STORAGE_KEY,
    );
  };

export const deleteLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch): void => {
    deleteLocalStorageHelper(
      dispatch,
      B2B_ACTIONS.DELETE_LOCAL_STORAGE,
      LOCAL_STORAGE_KEY,
    );
  };

export const fetchVoucher =
  (token: string, opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const dateTime = getExecutionDateTime(store());
    const frequency = store().b2b.get("frequency");

    const action = await asyncActionCreator(
      {
        url: buildUrl(B2B_VOUCHER_ENDPOINT)
          .setQuery("voucherToken", token)
          .setQuery(
            "firstJobExecutionDate",
            format(
              dateTime ? new Date(dateTime) : addDays(new Date(), 2),
              DATE_FORMAT.YEAR_MONTH_DAY_HOUR_MINUTE_SECOND_ISO_8601,
            ),
          )
          .setQuery("frequency", frequency, { optional: true })
          .done(),
        action: B2B_ACTIONS.FETCH_VOUCHER,
        params: {
          token,
        },
      },
      {
        ...(opts?.abortEnabled && { signal: abortControllers.fetchVoucher() }),
      },
    );

    return dispatch(action);
  };

export const resetVoucher = (): AnyAction => ({
  type: B2B_ACTIONS.RESET_VOUCHER,
});

export const setVoucher = (payload: VoucherInfo): AnyAction => ({
  type: B2B_ACTIONS.SET_VOUCHER,
  payload,
});

export const leaveProcess = (): AnyAction => ({
  type: B2B_ACTIONS.LEAVE_PROCESS,
});

export const getAQuote =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getGetAQuoteEndpointRequest(store());

    const action = await asyncActionCreator({
      url: GET_A_QUOTE_ENDPOINT,
      action: B2B_ACTIONS.GET_A_QUOTE,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const setAdditionalInfo = (payload: string): AnyAction => ({
  type: B2B_ACTIONS.SET_ADDITIONAL_INFO,
  payload,
});

export const fetchFrequencies =
  (serviceLocationId: number) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(B2B_ACTIONS.FETCH_FREQUENCIES));

    const action = await asyncActionCreator({
      url: buildUrl(B2B_FREQUENCIES_ENDPOINT)
        .setQuery("serviceLocationId", String(serviceLocationId))
        .done(),
      action: B2B_ACTIONS.FETCH_FREQUENCIES,
      method: ASYNC_ACTION_TYPES.GET,
    });

    return dispatch(action);
  };

export const setFrequency =
  (frequency: FREQUENCY, featureToggles?: FeatureTogglesType) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AnyAction> => {
    const serviceLocationId = store().b2b.get("serviceLocationId");

    const action: AnyAction = {
      type: B2B_ACTIONS.SET_FREQUENCY,
      payload: {
        frequency,
        serviceLocationId,
      },
      params: {
        featureToggles,
      },
    };

    return dispatch(action);
  };

export const fetchPlans =
  (serviceLocationId: number, opts?: ThunkActionOptions) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(B2B_ACTIONS.FETCH_PLANS));

    const action = await asyncActionCreator(
      {
        url: buildUrl(B2B_PLANS_ENDPOINT)
          .setQuery("serviceLocationId", String(serviceLocationId))
          .done(),
        action: B2B_ACTIONS.FETCH_PLANS,
        method: ASYNC_ACTION_TYPES.GET,
      },
      {
        ...(opts?.abortEnabled && { signal: abortControllers.fetchPlans() }),
      },
    );

    return dispatch(action);
  };
